import React from 'react';
import { Link } from 'react-router-dom';

interface DashboardCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  path?: string;
  comingSoon?: boolean;
}

const dashboardCards: DashboardCard[] = [
  {
    title: 'Growth Attribution',
    description: 'Cross-compare Instagram engagement with iOS installs and signups to see which posts drove spikes.',
    path: '/socials/attribution',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.306a11.95 11.95 0 015.814-5.518l2.74-1.22m0 0l-5.94-2.281m5.94 2.28l-2.28 5.941" />
      </svg>
    ),
  },
  {
    title: 'Stat Sheets',
    description: 'Top-5 business leaderboards by check-ins, live time, events, and more — exportable for social posts.',
    path: '/socials/stat-sheets',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.504-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
      </svg>
    ),
  },
  {
    title: 'Ambassador CRM',
    description: 'Manage ambassador relationships, outreach, and performance tracking.',
    comingSoon: true,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
      </svg>
    ),
  },
];

function SocialsPage(): React.ReactElement {
  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      {/* Header */}
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-8">
        <h1 className="text-3xl font-bold text-white font-display">Socials</h1>
        <p className="mt-2 text-gray-400 text-sm">
          Track social growth, attribution, and ambassador programs across the SocialSpark platform.
        </p>
      </div>

      {/* Card Grid */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {dashboardCards.map((card) => {
            const cardContent = (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    card.comingSoon
                      ? 'bg-gray-700/50 text-gray-500'
                      : 'bg-violet-600/20 text-violet-400'
                  }`}>
                    {card.icon}
                  </div>
                  {card.comingSoon && (
                    <span className="text-[11px] font-medium text-gray-500 bg-gray-700/50 px-2.5 py-1 rounded-full">
                      Coming soon
                    </span>
                  )}
                </div>
                <h3 className={`text-lg font-semibold mb-1.5 ${
                  card.comingSoon ? 'text-gray-500' : 'text-white'
                }`}>
                  {card.title}
                </h3>
                <p className={`text-sm leading-relaxed ${
                  card.comingSoon ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {card.description}
                </p>
                {!card.comingSoon && (
                  <div className="mt-4 flex items-center gap-1.5 text-sm font-medium text-violet-400">
                    View dashboard
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </div>
                )}
              </>
            );

            if (card.comingSoon) {
              return (
                <div
                  key={card.title}
                  className="bg-[#1a1a2e] border border-gray-800/50 rounded-xl p-6 opacity-60"
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <Link
                key={card.title}
                to={card.path!}
                className="bg-[#1a1a2e] border border-violet-500/20 rounded-xl p-6 transition-all duration-200 hover:border-violet-500/40 hover:shadow-lg hover:shadow-violet-900/20 cursor-pointer no-underline"
              >
                {cardContent}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SocialsPage;
